import { memo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
   Div,
   filterHover,
   Icon,
   Image,
   InputField,
   SideMenu,
   useBetterHtmlContext,
   useMediaQuery,
   useTheme,
} from "react-better-html";
import { useHotkeys } from "react-hotkeys-hook";

import { useProfileImage } from "../../../utils/hooks";

const sideMenuWidth = 300 + 80;

const headerButtonsSize = 46;

function AppHeader() {
   const theme = useTheme();
   const mediaQuery = useMediaQuery();
   const navigate = useNavigate();
   const location = useLocation();
   const profileImage = useProfileImage();
   const { app } = useBetterHtmlContext();

   const isOnAddPropertyPage = location.pathname === "/properties/add";

   const searchInputRef = useRef<HTMLInputElement>(null);

   const onClickProfileImage = useCallback(() => {
      navigate("/account");
   }, []);
   const onClickAddPropertyButton = useCallback(() => {
      if (isOnAddPropertyPage) navigate(-1);
      else {
         navigate("/properties/add", {
            viewTransition: true,
            state: {
               comingFromPathname: location.pathname,
            },
         });
      }
   }, [isOnAddPropertyPage, location]);

   const onPressSearchHotkey = useCallback(() => {
      searchInputRef.current?.focus();
   }, []);

   useHotkeys("mod+f", onPressSearchHotkey, {
      preventDefault: true,
   });

   return (
      <>
         <Div
            as="header"
            className="no-view-transition"
            position="fixed"
            width={mediaQuery.size1000 ? "100%" : `calc(100% - ${sideMenuWidth}px)`}
            height={theme.styles.headerHeight}
            top={0}
            left={mediaQuery.size1000 ? 0 : sideMenuWidth}
            backgroundImage={`linear-gradient(to bottom, ${theme.colors.backgroundBase}f0 50%, ${theme.colors.backgroundBase}00)`}
            padding={theme.styles.space}
            transition={theme.styles.transition}
            zIndex={9}
         >
            <Div.row height="100%" alignItems="center" justifyContent="space-between" gap={theme.styles.space}>
               <Div.row flex={1} alignItems="center" gap={theme.styles.space * 2}>
                  {mediaQuery.size1000 && <SideMenu.burgerButton color={theme.colors.textSecondary} />}

                  <InputField.search
                     maxWidth={app.contentMaxWidth / 2}
                     placeholder="search"
                     borderRadius={999}
                     ref={searchInputRef}
                  />
               </Div.row>

               <Div.row alignItems="center" gap={theme.styles.gap}>
                  <Image.profileImage
                     src={profileImage}
                     size={headerButtonsSize}
                     borderWidth={2}
                     filterHover={filterHover().z1}
                     cursor="pointer"
                     onClick={onClickProfileImage}
                  />

                  <Div.row
                     width={headerButtonsSize}
                     height={headerButtonsSize}
                     backgroundColor={isOnAddPropertyPage ? theme.colors.backgroundThree : theme.colors.textPrimary}
                     filterHover={isOnAddPropertyPage ? filterHover().z05 : filterHover().z1}
                     borderRadius={999}
                     alignItems="center"
                     justifyContent="center"
                     cursor="pointer"
                     onClick={onClickAddPropertyButton}
                  >
                     <Icon
                        name="plus"
                        color={isOnAddPropertyPage ? theme.colors.textPrimary : theme.colors.base}
                        size={18}
                        transform={`rotate(${isOnAddPropertyPage ? 45 : 0}deg)`}
                        transition={theme.styles.transition}
                     />
                  </Div.row>
               </Div.row>
            </Div.row>
         </Div>

         <Div width="100%" height={theme.styles.headerHeight} />
      </>
   );
}

export default memo(AppHeader);
